import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { fileUrl } from '../lib/api';
import { MIN_QTY } from '../lib/config';

const Cart = () => {
  const { items, updateQty, removeItem, subtotal, gst, total, getUnitPrice } = useCart();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div className="bg-gray-50 min-h-screen py-16">
        <div className="max-w-2xl mx-auto text-center px-4">
          <div className="bg-white rounded-lg border border-gray-200 p-12">
            <ShoppingBag className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h2 className="text-2xl font-bold text-gray-800">Your cart is empty</h2>
            <p className="text-gray-500 mt-2">Add some wholesale bargains to get started.</p>
            <Link to="/shop">
              <Button className="mt-6 bg-emerald-700 hover:bg-emerald-800 text-white">
                Start Shopping <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Shopping Cart ({items.length} items)</h1>

        <div className="grid md:grid-cols-[1fr_360px] gap-4 md:gap-6">
          <div className="space-y-3">
            {items.map((item) => {
              const unit = getUnitPrice(item, item.qty);
              return (
                <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-3 md:p-4 flex gap-3 md:gap-4">
                  <img src={fileUrl(item.image)} alt={item.name} className="w-20 h-20 md:w-24 md:h-24 rounded object-cover border border-gray-100 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${item.id}`} className="text-sm md:text-base font-medium text-gray-800 hover:text-emerald-700 line-clamp-2">
                      {item.name}
                    </Link>
                    <div className="mt-1 flex items-baseline gap-1.5 md:gap-2 flex-wrap">
                      <span className="text-base md:text-lg font-bold text-emerald-700">₹{unit.toFixed(2)}</span>
                      <span className="text-[10px] md:text-xs text-gray-400 line-through">₹{item.mrp.toFixed(2)}</span>
                      <span className="text-[9px] md:text-[10px] text-gray-500">(+GST)</span>
                    </div>
                    {item.tier1 && (
                      <div className="text-[9px] md:text-[10px] text-gray-500 mt-1 hidden sm:block">
                        {item.qty >= item.tier2.qty ? `Tier 2 (${item.tier2.qty}+) applied` : item.qty >= item.tier1.qty ? `Tier 1 (${item.tier1.qty}+) applied — buy ${item.tier2.qty - item.qty} more for tier 2` : `Buy ${item.tier1.qty - item.qty} more for tier 1 discount`}
                      </div>
                    )}
                    <div className="mt-2 md:mt-3 flex items-center gap-2 md:gap-3 flex-wrap">
                      <div className="flex items-center border border-gray-300 rounded">
                        <button
                          onClick={() => updateQty(item.id, Math.max(MIN_QTY, item.qty - 1))}
                          className="px-2 py-1 hover:bg-gray-100"
                          disabled={item.qty <= MIN_QTY}
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="px-3 text-sm font-medium">{item.qty}</span>
                        <button onClick={() => updateQty(item.id, item.qty + 1)} className="px-2 py-1 hover:bg-gray-100">
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <button onClick={() => removeItem(item.id)} className="text-red-500 hover:text-red-700 text-xs flex items-center gap-1">
                        <Trash2 className="w-3.5 h-3.5" /> Remove
                      </button>
                    </div>
                    <div className="mt-2 sm:hidden text-right">
                      <span className="text-base font-bold text-gray-800">₹{(unit * item.qty).toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="text-right hidden sm:block">
                    <div className="text-lg font-bold text-gray-800">₹{(unit * item.qty).toFixed(2)}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <aside>
            <div className="bg-white rounded-lg border border-gray-200 p-5 sticky top-32">
              <h3 className="font-semibold text-gray-800 mb-4">Order Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span className="font-medium">₹{subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">GST (18%)</span><span className="font-medium">₹{gst.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-gray-600">Shipping</span><span className="font-medium text-emerald-700">Free</span></div>
                <div className="border-t pt-2 flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-emerald-700">₹{total.toFixed(2)}</span>
                </div>
              </div>
              <Button onClick={() => navigate('/checkout')} className="w-full mt-5 bg-orange-500 hover:bg-orange-600 text-white">
                Proceed to Checkout <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Link to="/shop" className="block text-center text-xs text-emerald-700 hover:underline mt-3">
                Continue Shopping
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Cart;
